/**
 * Sticky add-to-cart bar (`<vitis-sticky-bar>`) for the Vitis product
 * information section.
 *
 * The bar wraps a real `product-form-component`, so add-to-cart uses the
 * theme's native cart flow (cart drawer updates, events, error handling).
 * This element only manages:
 * - variant / purchase-plan toggles → updates the embedded form inputs
 * - the quantity stepper → updates the form's quantity input
 * - positioning states, without ever mutating layout during scroll:
 *   - mobile (≤1200px): inline until the section sentinel scrolls past,
 *     then pinned to the viewport bottom. The anchor reserves the bar's
 *     height up front (ResizeObserver, outside the scroll path) so pinning
 *     causes zero layout shift — this is what fixes the random upward
 *     scroll jumps of the previous implementation.
 *   - desktop (≥1201px): always fixed; `data-at-top` / `data-overlaps-content`
 *     drive transparency. These only toggle attributes on a fixed element,
 *     which never reflows the document.
 */
import { Component } from '@theme/component';
import { getScrollEventTarget, scrollContainerMediaQuery } from '@theme/scroll-container';
import { ProductSelectEvent } from '@shopify/events';

const mobileQuery = window.matchMedia('(max-width: 1200px)');
const desktopQuery = window.matchMedia('(min-width: 1201px)');

/** Section elements checked for desktop bar overlap transparency. */
const OVERLAP_SELECTORS = '.vitis-product__image, .vitis-product__media, .vitis-product__circle-content';

/**
 * @typedef {Object} VariantData
 * @property {number} id
 * @property {boolean} available
 * @property {string[]} options
 * @property {string} price - Formatted price
 * @property {string | null} compare_at_price - Formatted compare-at price
 * @property {{ min: number, max: number | null, increment: number }} quantity_rule
 * @property {number} inventory_quantity
 * @property {string | null} inventory_management
 * @property {string} inventory_policy
 * @property {string | null} media
 * @property {number | null} [selling_plan_id]
 * @property {string | null} [selling_plan_price] - Formatted allocation price
 * @property {string | null} [selling_plan_compare_at_price] - Formatted allocation compare-at
 * @property {boolean} [requires_selling_plan]
 */

/** @typedef {'in-stock' | 'low-stock' | 'last-bottles' | 'out-of-stock'} StockStatus */

/**
 * @typedef {Object} VitisStickyBarRefs
 * @property {HTMLElement} bar - The bar container
 */

/**
 * @extends {Component<VitisStickyBarRefs>}
 */
class VitisStickyBar extends Component {
  requiredRefs = ['bar'];

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {VariantData[]} */
  #variants = [];

  /** @type {Map<number, string>} */
  #selectedOptions = new Map();

  /** @type {IntersectionObserver | null} */
  #pinObserver = null;

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  /** @type {EventTarget | null} */
  #scrollTarget = null;

  /** @type {number | null} */
  #scrollFrame = null;

  /** @type {number} */
  #reservedHeight = 0;

  /** @type {MutationObserver | null} */
  #pickupObserver = null;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;

    this.#parseVariantData();
    this.#readSelectedOptions();
    this.#syncSellingPlanForVariant(this.#findSelectedVariant());
    this.#observePickupAvailability();

    scrollContainerMediaQuery.addEventListener('change', this.#handleBreakpointChange, { signal });
    mobileQuery.addEventListener('change', this.#handleBreakpointChange, { signal });

    this.#setupHeightObserver();
    this.#handleBreakpointChange();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#pinObserver?.disconnect();
    this.#pinObserver = null;
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
    this.#pickupObserver?.disconnect();
    this.#pickupObserver = null;
    this.#unbindScrollListener();
    if (this.#scrollFrame) cancelAnimationFrame(this.#scrollFrame);
    this.#abortController.abort();

    if (!document.querySelector('vitis-sticky-bar')) {
      document.body.style.removeProperty('--vitis-sticky-bar-height');
    }
  }

  // ---------------------------------------------------------------------------
  // Variant selection
  // ---------------------------------------------------------------------------

  /** @param {PointerEvent} event */
  handleOptionSelect = (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-option-value]') : null;
    if (!(button instanceof HTMLElement)) return;

    const position = Number(button.dataset.optionPosition);
    const value = button.dataset.optionValue;
    if (!position || value == null) return;

    const group = button.closest('.vitis-sticky-bar__toggle');
    group?.querySelectorAll('.vitis-sticky-bar__toggle-item').forEach((item) => {
      item.classList.toggle('is-active', item === button);
    });

    this.#selectedOptions.set(position, value);
    this.#applyVariant(this.#findSelectedVariant());
  };

  /** @param {Event} event */
  handleOptionSelectChange = (event) => {
    const select = event.target;
    if (!(select instanceof HTMLSelectElement)) return;

    const position = Number(select.dataset.optionPosition);
    if (!position) return;

    this.#selectedOptions.set(position, select.value);
    this.#applyVariant(this.#findSelectedVariant());
  };

  /** @param {PointerEvent} event */
  handlePlanSelect = (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-plan]') : null;
    if (!(button instanceof HTMLButtonElement) || !button.dataset.plan || button.disabled) return;

    this.dataset.plan = button.dataset.plan;

    const sellingPlanInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[name="selling_plan"]')
    );
    if (sellingPlanInput) {
      sellingPlanInput.value = button.dataset.plan === 'subscription' ? (button.dataset.sellingPlanId ?? '') : '';
    }

    this.#updateCtaPrice(this.#findSelectedVariant());
  };

  #parseVariantData() {
    const script = this.querySelector('script[data-variant-data]');
    if (!script?.textContent) return;

    try {
      this.#variants = JSON.parse(script.textContent);
    } catch (error) {
      console.warn('[vitis-sticky-bar] Failed to parse variant data:', error);
    }
  }

  /** Seeds the selected options map from the server-rendered controls. */
  #readSelectedOptions() {
    this.querySelectorAll('.vitis-sticky-bar__toggle-item.is-active[data-option-value]').forEach((button) => {
      if (!(button instanceof HTMLElement)) return;
      this.#selectedOptions.set(Number(button.dataset.optionPosition), button.dataset.optionValue ?? '');
    });

    this.querySelectorAll('select[data-option-position]').forEach((select) => {
      if (!(select instanceof HTMLSelectElement)) return;
      this.#selectedOptions.set(Number(select.dataset.optionPosition), select.value);
    });
  }

  /** @returns {VariantData | undefined} */
  #findSelectedVariant() {
    return this.#variants.find((variant) =>
      [...this.#selectedOptions.entries()].every(([position, value]) => variant.options[position - 1] === value)
    );
  }

  /** @returns {{ inStock: number, lowStockMin: number, lastBottles: number }} */
  #getStockThresholds() {
    const inStock = Number(this.dataset.stockThresholdInStock);
    const lowStockMin = Number(this.dataset.stockThresholdLowStockMin);
    const lastBottles = Number(this.dataset.stockThresholdLastBottles);

    const resolvedLastBottles = Number.isFinite(lastBottles) ? lastBottles : 6;
    let resolvedLowStockMin = Number.isFinite(lowStockMin) ? lowStockMin : 7;

    if (resolvedLowStockMin <= resolvedLastBottles) {
      resolvedLowStockMin = resolvedLastBottles + 1;
    }

    return {
      inStock: Number.isFinite(inStock) ? inStock : 24,
      lowStockMin: resolvedLowStockMin,
      lastBottles: resolvedLastBottles,
    };
  }

  /** @param {VariantData | undefined} variant @returns {StockStatus} */
  #resolveStockStatus(variant) {
    if (!variant) return 'out-of-stock';

    if (variant.inventory_management === 'shopify') {
      const quantity = variant.inventory_quantity ?? 0;
      const { inStock, lowStockMin } = this.#getStockThresholds();

      if (quantity > inStock) return 'in-stock';
      if (quantity >= lowStockMin) return 'low-stock';
      if (quantity >= 1) return 'last-bottles';
      return 'out-of-stock';
    }

    return variant.available ? 'in-stock' : 'out-of-stock';
  }

  /** @param {VariantData | undefined} variant */
  #applyVariant(variant) {
    const variantIdInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
    const cta = /** @type {HTMLButtonElement | null} */ (this.querySelector('.vitis-sticky-bar__cta'));
    const ctaLabel = this.querySelector('.vitis-sticky-bar__cta-label');
    const ctaDot = /** @type {HTMLElement | null} */ (this.querySelector('.vitis-sticky-bar__cta-dot'));
    const stockBadge = this.querySelector('.vitis-sticky-bar__stock');

    const available = variant?.available === true;
    this.dataset.variantAvailable = String(available);

    if (variantIdInput) variantIdInput.value = variant ? String(variant.id) : '';
    stockBadge?.setAttribute('data-stock-status', this.#resolveStockStatus(variant));
    this.#syncSellingPlanForVariant(variant);

    if (cta) cta.disabled = !available;
    if (ctaLabel) {
      if (!variant) {
        ctaLabel.textContent = this.dataset.textUnavailable ?? '';
      } else if (!available) {
        ctaLabel.textContent = this.dataset.textSoldOut ?? '';
      } else {
        ctaLabel.textContent = this.dataset.ctaLabel ?? '';
      }
    }
    if (ctaDot) ctaDot.hidden = !available;
    this.#updateCtaPrice(variant);
    this.#updateFormaatBadge(variant);

    if (variant) {
      this.dataset.quantityMin = String(variant.quantity_rule.min);
      this.dataset.quantityIncrement = String(variant.quantity_rule.increment);
      if (variant.quantity_rule.max != null) {
        this.dataset.quantityMax = String(variant.quantity_rule.max);
      } else {
        delete this.dataset.quantityMax;
      }
      this.#setQuantity(this.#getQuantity());

      const addToCart = this.querySelector('add-to-cart-component');
      if (addToCart instanceof HTMLElement && variant.media) {
        addToCart.dataset.productVariantMedia = variant.media;
      }

      const url = new URL(window.location.href);
      url.searchParams.set('variant', String(variant.id));
      window.history.replaceState({}, '', url.toString());

      this.#dispatchProductSelect(variant);
    }
  }

  /**
   * Keeps the "Inhoud" info-pilletje (Magnum / 37,5 cl / Jeroboam / 75 cl) in
   * sync with the selected variant. The badge element itself is rendered by
   * vitis-productinformatie.liquid with a `data-formaat-position` attribute
   * (1-based, matching Shopify's option1/2/3 convention) so this stays
   * correct regardless of which option position "Flesformaat" occupies.
   * Falls back to "75 cl" for a standard bottle instead of hiding — it's one
   * of three pilletjes (Jaargang / Inhoud / Type) shown side by side, so it
   * should always show a value, not disappear for the common case.
   * @param {VariantData | undefined} variant
   */
  #updateFormaatBadge(variant) {
    const section = this.closest('vitis-product-information');
    const badge = section?.querySelector('[data-vitis-formaat-badge]');
    if (!(badge instanceof HTMLElement)) return;

    const position = Number(badge.dataset.formaatPosition);
    const rawValue = position > 0 ? (variant?.options[position - 1] ?? '') : '';

    let formaat = '75 cl';
    if (rawValue.includes('Magnum')) {
      formaat = 'Magnum';
    } else if (rawValue.includes('37,5') || rawValue.includes('37.5')) {
      formaat = '37,5 cl';
    } else if (rawValue.includes('Jeroboam')) {
      formaat = 'Jeroboam';
    }

    badge.textContent = formaat;
    badge.hidden = false;
  }

  /**
   * Keep purchase-option UI and `selling_plan` input aligned with the selected
   * variant's allocations (Shopify: not every variant may share the same plans).
   * @param {VariantData | undefined} variant
   */
  #syncSellingPlanForVariant(variant) {
    const subscriptionButton = /** @type {HTMLButtonElement | null} */ (
      this.querySelector('.vitis-sticky-bar__toggle-item--subscription')
    );
    const sellingPlanInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[name="selling_plan"]')
    );
    if (!subscriptionButton && !sellingPlanInput) return;

    const planId = variant?.selling_plan_id != null ? String(variant.selling_plan_id) : '';
    const requiresPlan = variant?.requires_selling_plan === true;
    const hasPlan = planId !== '';

    if (subscriptionButton) {
      subscriptionButton.dataset.sellingPlanId = planId;
      subscriptionButton.disabled = !hasPlan;
    }

    let plan = this.dataset.plan || 'one-time';
    if (requiresPlan && hasPlan) {
      plan = 'subscription';
    } else if (plan === 'subscription' && !hasPlan) {
      plan = 'one-time';
    }

    this.dataset.plan = plan;

    if (sellingPlanInput) {
      sellingPlanInput.value = plan === 'subscription' ? planId : '';
    }
  }

  /**
   * CTA price follows the selected purchase option: one-time uses variant price;
   * subscription uses `selling_plan_allocation` (plan discount baked into price).
   * @param {VariantData | undefined} variant
   */
  #updateCtaPrice(variant) {
    const ctaCompare = /** @type {HTMLElement | null} */ (this.querySelector('.vitis-sticky-bar__cta-compare'));
    const ctaPrice = /** @type {HTMLElement | null} */ (this.querySelector('.vitis-sticky-bar__cta-price'));
    if (!ctaPrice) return;

    const available = variant?.available === true;
    ctaPrice.hidden = !available;

    if (!available || !variant) {
      if (ctaCompare) {
        ctaCompare.hidden = true;
        ctaCompare.textContent = '';
      }
      ctaPrice.textContent = '';
      return;
    }

    const usePlan =
      this.dataset.plan === 'subscription' && Boolean(variant.selling_plan_price);
    const price = usePlan ? (variant.selling_plan_price ?? '') : (variant.price ?? '');
    const compareAt = usePlan
      ? (variant.selling_plan_compare_at_price ?? null)
      : (variant.compare_at_price ?? null);

    ctaPrice.textContent = price;

    if (ctaCompare) {
      const hasCompare = Boolean(compareAt);
      ctaCompare.hidden = !hasCompare;
      ctaCompare.textContent = hasCompare ? (compareAt ?? '') : '';
    }
  }

  /** @param {VariantData} variant */
  #dispatchProductSelect(variant) {
    const section = this.closest('.shopify-section, dialog, vitis-product-information');
    if (!section) return;

    const deferredEventPromise = ProductSelectEvent.createPromise();

    section.dispatchEvent(
      new ProductSelectEvent({
        product: {
          id: this.dataset.productId ?? '',
          title: '',
          handle: '',
        },
        selectedOptions: [],
        promise: deferredEventPromise.promise,
      })
    );

    deferredEventPromise.resolve({
      variant: null,
      detail: {
        resource: {
          id: String(variant.id),
          available: variant.available,
        },
      },
    });

    this.#syncPickupMeta();
  }

  #observePickupAvailability() {
    const localPickup = this.querySelector('local-pickup');
    if (!(localPickup instanceof HTMLElement)) return;

    this.#pickupObserver = new MutationObserver(() => {
      this.#syncPickupMeta();
    });

    this.#pickupObserver.observe(localPickup, { attributes: true, attributeFilter: ['hidden'] });
  }

  #syncPickupMeta() {
    const pickupMeta = this.querySelector('[data-pickup-meta]');
    const localPickup = this.querySelector('local-pickup');
    if (!(pickupMeta instanceof HTMLElement) || !(localPickup instanceof HTMLElement)) return;

    pickupMeta.hidden = localPickup.hasAttribute('hidden');
  }

  // ---------------------------------------------------------------------------
  // Quantity stepper
  // ---------------------------------------------------------------------------

  handleQuantityDecrease = () => {
    this.#setQuantity(this.#getQuantity() - this.#quantityIncrement());
  };

  handleQuantityIncrease = () => {
    this.#setQuantity(this.#getQuantity() + this.#quantityIncrement());
  };

  #quantityIncrement() {
    return Number(this.dataset.quantityIncrement) || 1;
  }

  #getQuantity() {
    const input = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="quantity"]'));
    return Number(input?.value) || 1;
  }

  /** @param {number} quantity */
  #setQuantity(quantity) {
    const min = Number(this.dataset.quantityMin) || 1;
    const max = this.dataset.quantityMax ? Number(this.dataset.quantityMax) : Infinity;
    const clamped = Math.min(Math.max(quantity, min), max);

    const input = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="quantity"]'));
    const display = this.querySelector('.vitis-sticky-bar__stepper-value');

    if (input) input.value = String(clamped);
    if (display) display.textContent = String(clamped);
  }

  // ---------------------------------------------------------------------------
  // Positioning
  // ---------------------------------------------------------------------------

  #handleBreakpointChange = () => {
    this.#setupPinObserver();
    this.#setupDesktopScrollListener();
  };

  /**
   * Reserves the bar's height on the anchor and exposes it to the page
   * (footer offset). Runs from ResizeObserver only — never inside scroll.
   */
  #setupHeightObserver() {
    this.#resizeObserver = new ResizeObserver(() => {
      const { bar } = this.refs;
      if (!(bar instanceof HTMLElement)) return;

      // Only measure the in-flow bar; a pinned (fixed) bar has the same
      // rendered height, so the reserved value stays valid.
      const height = Math.ceil(bar.getBoundingClientRect().height);
      if (height === 0 || Math.abs(height - this.#reservedHeight) < 2) return;

      this.#reservedHeight = height;
      this.style.setProperty('--vitis-sticky-bar-height', `${height}px`);
      document.body.style.setProperty('--vitis-sticky-bar-height', `${height}px`);
    });

    this.#resizeObserver.observe(this.refs.bar);
  }

  /** Pins the bar (mobile) once the section sentinel scrolls above the viewport. */
  #setupPinObserver() {
    this.#pinObserver?.disconnect();
    this.#pinObserver = null;

    if (!mobileQuery.matches) {
      this.#setPinned(false);
      return;
    }

    const section = this.closest('vitis-product-information');
    const sentinel = section?.querySelector('[data-sticky-sentinel]');
    if (!(sentinel instanceof HTMLElement)) return;

    this.#pinObserver = new IntersectionObserver(
      ([entry]) => {
        if (!entry || !mobileQuery.matches) return;
        this.#setPinned(!entry.isIntersecting && entry.boundingClientRect.top < 0);
      },
      { threshold: 0 }
    );

    this.#pinObserver.observe(sentinel);
  }

  /** @param {boolean} pinned */
  #setPinned(pinned) {
    this.refs.bar?.setAttribute('data-pinned', String(pinned));
    this.classList.toggle('is-pinned', pinned);
  }

  /** Desktop transparency states — attribute toggles only, no layout writes. */
  #setupDesktopScrollListener() {
    this.#unbindScrollListener();

    if (!desktopQuery.matches) return;

    this.#scrollTarget = getScrollEventTarget();
    this.#scrollTarget.addEventListener('scroll', this.#requestDesktopStateUpdate, { passive: true });
    this.#requestDesktopStateUpdate();
  }

  #unbindScrollListener() {
    this.#scrollTarget?.removeEventListener('scroll', this.#requestDesktopStateUpdate);
    this.#scrollTarget = null;
  }

  #requestDesktopStateUpdate = () => {
    if (this.#scrollFrame) return;

    this.#scrollFrame = requestAnimationFrame(() => {
      this.#scrollFrame = null;
      this.#updateDesktopState();
    });
  };

  #updateDesktopState = () => {
    if (!desktopQuery.matches) return;

    const { bar } = this.refs;
    const section = this.closest('vitis-product-information');
    if (!(bar instanceof HTMLElement) || !(section instanceof HTMLElement)) return;

    const barRect = bar.getBoundingClientRect();
    const sectionRect = section.getBoundingClientRect();
    bar.dataset.atTop = String(sectionRect.bottom > barRect.top);
    bar.dataset.overlapsContent = String(this.#overlapsContent(barRect, section));
  };

  /**
   * @param {DOMRect} barRect
   * @param {HTMLElement} section
   * @returns {boolean}
   */
  #overlapsContent(barRect, section) {
    for (const target of section.querySelectorAll(OVERLAP_SELECTORS)) {
      if (!(target instanceof HTMLElement)) continue;

      const style = getComputedStyle(target);
      if (style.display === 'none' || style.visibility === 'hidden') continue;
      if (Number.parseFloat(style.opacity) < 0.01) continue;

      const rect = target.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) continue;

      if (barRect.top < rect.bottom && barRect.bottom > rect.top && barRect.left < rect.right && barRect.right > rect.left) {
        return true;
      }
    }

    return false;
  }
}

if (!customElements.get('vitis-sticky-bar')) {
  customElements.define('vitis-sticky-bar', VitisStickyBar);
}